// Configuration API for AidFone Android App
// This module handles sending configuration data to the Android app

import type {
  AidFoneConfig,
  DeployConfig,
  ProfileType,
  QRCodeConfig,
  DeviceRegistration,
} from '../types';

const API_BASE_URL: string = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * API response type
 */
export interface ApiResponse {
  success: boolean;
  message?: string;
  data?: unknown;
}

/**
 * Build payload for deployment
 */
interface DeployPayload {
  userId: string;
  profileType: ProfileType;
  templateId: string;
  enabledFeatures: string[];
  accessibility: {
    largeButtons: boolean;
    highContrast: boolean;
    voiceControl: boolean;
    simplifiedUI: boolean;
    textSize: 'normal' | 'extra-large';
    textSizePercent: number;
  };
  version: number;
  timestamp: string;
}

/**
 * Send configuration to Android device
 * @param deviceId - The Android device ID or user account ID
 * @param config - Configuration object
 */
export const deployConfiguration = async (
  deviceId: string,
  config: DeployConfig
): Promise<ApiResponse> => {
  // Determine text size from config - convert percentage to string format
  const getTextSize = (): 'normal' | 'extra-large' => {
    if (config.accessibility?.textSize) {
      // textSize is a percentage (100, 125, 150, 175, 200)
      return config.accessibility.textSize >= 150 ? 'extra-large' : 'normal';
    }
    return config.features.includes('largeText') ? 'extra-large' : 'normal';
  };

  const payload: DeployPayload = {
    userId: deviceId,
    profileType: config.profile,
    templateId: config.templateId,
    enabledFeatures: config.features,
    accessibility: {
      largeButtons: config.features.includes('largeButtons'),
      highContrast: config.accessibility?.highContrast ?? config.features.includes('contrast'),
      voiceControl: config.accessibility?.voiceCommands ?? config.features.includes('voice'),
      simplifiedUI: config.features.includes('simplifiedUI') || config.features.includes('simplifiedInterface'),
      textSize: getTextSize(),
      textSizePercent: config.accessibility?.textSize ?? 100,
    },
    version: Date.now(),
    timestamp: new Date().toISOString(),
  };

  const url = `${API_BASE_URL}/devices/${deviceId}/config`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to deploy configuration: ${response.statusText}`);
    }

    const result: ApiResponse = await response.json();
    return result;
  } catch (error) {
    console.error('Error deploying configuration:', error);
    throw error;
  }
};

/**
 * Get current configuration from device
 * @param deviceId - The Android device ID
 */
export const getDeviceConfiguration = async (deviceId: string): Promise<AidFoneConfig> => {
  try {
    const response = await fetch(`${API_BASE_URL}/devices/${deviceId}/config`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get configuration: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting configuration:', error);
    throw error;
  }
};

/**
 * Register device for push notifications (Firebase Cloud Messaging)
 * @param deviceId - The Android device ID
 * @param fcmToken - Firebase Cloud Messaging token
 */
export const registerDevice = async (
  deviceId: string,
  fcmToken: string
): Promise<ApiResponse> => {
  try {
    const body: DeviceRegistration = {
      deviceId,
      fcmToken,
      platform: 'android',
    };

    const response = await fetch(`${API_BASE_URL}/devices/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Failed to register device: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error registering device:', error);
    throw error;
  }
};

/**
 * Send configuration via QR code data
 * Generates a QR code that the Android app can scan to receive configuration
 */
export const generateConfigQRCode = (config: DeployConfig): string => {
  const qrData: QRCodeConfig = {
    type: 'AIDFONE_CONFIG',
    version: '1.0',
    config: {
      profileType: config.profile,
      templateId: config.templateId,
      features: config.features,
      timestamp: new Date().toISOString(),
    },
    serverUrl: API_BASE_URL,
  };

  return JSON.stringify(qrData);
};
